import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "./api.js";
import { PACKS, CURRENCIES, detectCurrency, formatPrice, formatPerScan } from "./packs.js";
import LandingPage from "./LandingPage.jsx";
import PricingPage from "./PricingPage.jsx";
import Turnstile from "./Turnstile.jsx";
import HelpPage from "./HelpPage.jsx";
import PrivacyPage from "./PrivacyPage.jsx";
import TermsPage from "./TermsPage.jsx";
import RefundsPage from "./RefundsPage.jsx";
import ContactPage from "./ContactPage.jsx";
import BrandLockup from "./BrandLockup.jsx";
import { readTheme, applyTheme, nextTheme, THEME_LABEL } from "./theme.js";
import { PAGE_PATH, PATH_PAGE, applyPageSeo } from "./pageSeo.js";
import { useT } from "./i18n/I18nContext.jsx";
import LanguageSwitcher from "./LanguageSwitcher.jsx";
import {
  SunIcon,
  MoonIcon,
  MonitorIcon,
  LogoutIcon,
  ScanIcon,
  CameraIcon,
  FolderIcon,
  CarIcon,
  PinIcon,
  RulerIcon,
  GearIcon,
  FilterIcon,
  MapIcon,
  PencilIcon,
  XIcon,
  CheckCircleIcon,
  DownloadIcon,
  TableIcon,
  MailIcon,
  ArchiveIcon,
  UnarchiveIcon,
} from "./Icons.jsx";

const CURRENT_YEAR = new Date().getFullYear();
// Must match backend/src/wallet.js MILLICREDITS_PER_SCAN.
const MILLICREDITS_PER_SCAN = 30;
// 3 scans of runway left.
const LOW_BALANCE_THRESHOLD = MILLICREDITS_PER_SCAN * 3;

const PUBLIC_PAGES = {
  pricing: PricingPage,
  help: HelpPage,
  privacy: PrivacyPage,
  terms: TermsPage,
  refunds: RefundsPage,
  contact: ContactPage,
};

export default function App() {
  const [theme, setTheme] = useState(readTheme);
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const [user, setUser] = useState(undefined); // undefined = loading
  // 'landing' | 'trial' | 'login' | 'help' | 'privacy' | 'terms' | 'refunds' | 'contact'
  // Seeded from the URL so a direct load of e.g. /pricing renders pricing
  // content instead of always falling back to the landing page — that
  // mismatch (every public URL rendering identical landing content, with no
  // canonical tag) was why Google flagged these as duplicate pages.
  const [page, setPage] = useState(() => PATH_PAGE[window.location.pathname] ?? "landing");

  // Keeps the URL bar in sync with client-only page state, and updates
  // title/description/canonical/robots to match. Use this instead of
  // setPage directly for anything a user could reasonably deep-link to,
  // bookmark, or that a crawler should see as its own URL.
  function navigate(id) {
    setPage(id);
    const path = PAGE_PATH[id];
    if (path && window.location.pathname !== path) {
      window.history.pushState({ page: id }, "", path);
    }
  }

  useEffect(() => {
    function onPopState() {
      setPage(PATH_PAGE[window.location.pathname] ?? "landing");
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    applyPageSeo(page);
  }, [page]);
  // A file picked from the landing page's "try it free" CTA — scanned
  // immediately (anonymously, no account) so the result shows right away.
  // Carried through into the login flow and auto-submitted for real (using
  // the account's free trial) once signed in, so the visitor sees the
  // result first and only signs in to keep it.
  const [pendingScanFile, setPendingScanFile] = useState(null);
  const [trialResult, setTrialResult] = useState(null);
  const [trialScanning, setTrialScanning] = useState(false);
  const [trialError, setTrialError] = useState("");
  // Mounted once alongside the landing page (not per-click) so a token is
  // usually already sitting there by the time a visitor finishes picking a
  // photo. Held in a ref, not state, since we only ever read it at submit
  // time — no re-render needed when a fresh token arrives.
  const previewTurnstileRef = useRef(null);
  const previewTokenRef = useRef(null);

  useEffect(() => {
    api
      .me()
      .then(setUser)
      .catch(() => setUser(null));
  }, []);

  function waitForPreviewToken(timeoutMs = 4000) {
    return new Promise((resolve) => {
      const start = Date.now();
      (function poll() {
        if (previewTokenRef.current || Date.now() - start > timeoutMs) {
          resolve(previewTokenRef.current);
        } else {
          setTimeout(poll, 150);
        }
      })();
    });
  }

  async function pickFile(file) {
    setPendingScanFile(file);
    setTrialResult(null);
    setTrialError("");
    setTrialScanning(true);
    setPage("trial");
    try {
      const token = await waitForPreviewToken();
      const ocr = await api.previewScan(file, token);
      previewTokenRef.current = null;
      previewTurnstileRef.current?.reset();
      setTrialResult(ocr);
    } catch (err) {
      if (err.status === 429) {
        // Used up the 3 free anonymous previews — straight to login instead
        // of an error. The file is still held (pendingScanFile), so signing
        // in submits it for real via the account's own free trial.
        navigate("login");
        return;
      }
      setTrialError(err.message);
    } finally {
      setTrialScanning(false);
    }
  }

  function resetTrial() {
    setPendingScanFile(null);
    setTrialResult(null);
    setTrialError("");
    navigate("landing");
  }

  if (user === undefined) return null;

  if (!user) {
    // The scan-preview Turnstile widget is mounted here, OUTSIDE the
    // per-page branches below, so it survives the landing -> trial page
    // transition pickFile() triggers. It used to live inside the landing
    // branch only, which unmounted (and cancelled) the widget the instant
    // setPage("trial") ran — often before the background verification had
    // finished — so every scan lost its token and got rejected.
    let pageEl;
    if (page === "login") {
      pageEl = (
        <LoginView
          onLoggedIn={(u) => {
            // Login can happen from a deep-linked page (/pricing, /login,
            // etc.) — once signed in, the dashboard renders regardless of
            // `page`, so drop the stale public-page URL back to "/".
            if (window.location.pathname !== "/") {
              window.history.replaceState(null, "", "/");
            }
            // The tab title/meta tags are only refreshed by the effect keyed
            // on `page` (applyPageSeo), which doesn't change here — without
            // this the browser tab kept showing "Sign in | ..." for the rest
            // of the logged-in session.
            applyPageSeo("landing");
            setUser(u);
          }}
          onBack={resetTrial}
          pendingScanFile={pendingScanFile}
        />
      );
    } else if (page === "trial") {
      pageEl = (
        <TrialResultView
          scanning={trialScanning}
          result={trialResult}
          error={trialError}
          onSignIn={() => navigate("login")}
          onBack={resetTrial}
        />
      );
    } else {
      const PageComponent = PUBLIC_PAGES[page];
      pageEl = PageComponent ? (
        <PageComponent
          onNavigate={navigate}
          onGetStarted={() => navigate("login")}
          theme={theme}
          setTheme={setTheme}
        />
      ) : (
        <LandingPage
          onGetStarted={() => navigate("login")}
          onNavigate={navigate}
          onPickFile={pickFile}
          theme={theme}
          setTheme={setTheme}
        />
      );
    }
    return (
      <>
        {/* This widget usually renders nothing (appearance: "interaction-only"
            — Cloudflare only shows UI if it decides a challenge is actually
            needed). It's mounted here, outside any page's own layout, so on
            the rare occasion it DOES need to show, this fixed corner slot
            keeps it from just appearing as a stray unstyled box wherever it
            happens to sit in the DOM. */}
        <div className="floating-turnstile">
          <Turnstile
            ref={previewTurnstileRef}
            action="scan_preview"
            invisible
            onToken={(t) => {
              previewTokenRef.current = t;
            }}
          />
        </div>
        {pageEl}
      </>
    );
  }

  // Stripe redirects here after checkout — success_url/cancel_url both point
  // at the SPA's catch-all (no real router), so detect which one via the
  // path once, then clean the URL so a refresh doesn't re-trigger it.
  const purchaseJustCompleted = window.location.pathname === "/buy/success";
  const returnedFromCheckout =
    purchaseJustCompleted || window.location.pathname === "/buy";
  if (returnedFromCheckout) {
    window.history.replaceState(null, "", "/");
  }

  return (
    <ExpenseApp
      user={user}
      theme={theme}
      setTheme={setTheme}
      onLoggedOut={() =>
        api.logout().finally(() => {
          // Logging out only clears `user`, not the landing/trial page
          // state machine — if the account had earlier gone through the
          // anonymous scan-preview flow, `page` could still be stuck on
          // "trial" with no scan in progress, no error, no result: an
          // empty shell. resetTrial() puts it back on the actual landing
          // page every time, not just for the trial flow's own use.
          resetTrial();
          setUser(null);
        })
      }
      purchaseJustCompleted={purchaseJustCompleted}
      returnedFromCheckout={returnedFromCheckout}
      autoScanFile={pendingScanFile}
      onAutoScanHandled={() => setPendingScanFile(null)}
    />
  );
}

// Passwordless: enter your email, we send a 6-digit code, enter the code.
// The first successful code for a new email creates the account (no
// separate signup step).
function LoginView({ onLoggedIn, onBack, pendingScanFile }) {
  const t = useT();
  const [step, setStep] = useState("email"); // 'email' | 'code'
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const [googleEnabled, setGoogleEnabled] = useState(false);

  useEffect(() => {
    api
      .authConfig()
      .then((c) => setGoogleEnabled(!!c.googleEnabled))
      .catch(() => {});
  }, []);

  async function sendCode(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.sendLoginCode(email, turnstileToken);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      setStep("code");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await api.verifyLoginCode(email, pin);
      onLoggedIn(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app login-app">
      <header>
        <BrandLockup />
      </header>
      <main>
        {step === "email" ? (
          <form className="card login-form" onSubmit={sendCode}>
            {pendingScanFile ? (
              <>
                <h3>{t("app.login.oneMoreStepTitle")}</h3>
                <p className="muted">{t("app.login.oneMoreStepSub")}</p>
              </>
            ) : (
              <>
                <h3>{t("app.login.signInTitle")}</h3>
                <p className="muted">{t("app.login.signInSub")}</p>
              </>
            )}
            {error && <div className="error">{error}</div>}

            {googleEnabled && !pendingScanFile && (
              <>
                <a href="/api/auth/google" className="secondary-link login-block">
                  {t("app.login.continueWithGoogle")}
                </a>
                <div className="login-divider">
                  <span>{t("app.login.or")}</span>
                </div>
              </>
            )}

            <Field label={t("app.login.fieldEmail")}>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
            <Turnstile ref={turnstileRef} action="login_code" onToken={setTurnstileToken} />
            <button className="primary block" disabled={busy || !turnstileToken}>
              {busy ? t("app.login.sending") : t("app.login.sendCode")}
            </button>
            {onBack && (
              <button type="button" className="secondary block" onClick={onBack}>
                {t("app.back")}
              </button>
            )}
          </form>
        ) : (
          <form className="card login-form" onSubmit={verifyCode}>
            <h3>{t("app.login.enterCodeTitle")}</h3>
            <p className="muted">{t("app.login.enterCodeSub", { email })}</p>
            {error && <div className="error">{error}</div>}
            <Field label={t("app.login.fieldCode")}>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                required
              />
            </Field>
            <button className="primary block" disabled={busy}>
              {busy ? t("app.login.verifying") : t("common.signIn")}
            </button>
            <button
              type="button"
              className="secondary block"
              onClick={() => {
                setStep("email");
                setPin("");
                setError("");
              }}
            >
              {t("app.login.useDifferentEmail")}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}

// Shows the anonymous OCR result — no account, nothing saved yet — right
// after picking a photo from the landing page's "try it free" CTA. Signing
// in re-runs the real scan for real (see App(): pendingScanFile carries the
// same file through, ScanView's autoScanFile effect submits it once logged
// in), so nothing here needs to persist anything itself.
function TrialResultView({ scanning, result, error, onSignIn, onBack }) {
  const t = useT();
  return (
    <div className="app login-app">
      <header>
        <BrandLockup />
      </header>
      <main>
        <div className="card login-form">
          {scanning && (
            <>
              <h3>{t("app.trial.readingTitle")}</h3>
              <p className="muted">{t("app.trial.readingSub")}</p>
            </>
          )}

          {!scanning && error && (
            <>
              <h3>{t("app.trial.errorTitle")}</h3>
              <div className="error">{error}</div>
              <button className="secondary block" onClick={onBack}>
                {t("app.trial.tryDifferentPhoto")}
              </button>
            </>
          )}

          {!scanning && result && (
            <>
              <h3>{t("app.trial.resultTitle")}</h3>
              <p className="muted">{t("app.trial.resultSub")}</p>
              <dl className="trial-result">
                <dt>{t("app.trial.vendor")}</dt>
                <dd>{result.vendor || "—"}</dd>
                <dt>{t("app.trial.date")}</dt>
                <dd>{result.expense_date || "—"}</dd>
                <dt>{t("app.trial.amount")}</dt>
                <dd>
                  {result.amount ? `${result.currency} ${result.amount}` : "—"}
                </dd>
                <dt>{t("app.trial.category")}</dt>
                <dd>{result.category || "—"}</dd>
              </dl>
              <button className="primary block" onClick={onSignIn}>
                {t("app.trial.signInToSave")}
              </button>
              <button className="secondary block" onClick={onBack}>
                {t("app.trial.tryDifferentPhoto")}
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function ExpenseApp({
  user,
  onLoggedOut,
  purchaseJustCompleted,
  returnedFromCheckout,
  autoScanFile,
  onAutoScanHandled,
  theme,
  setTheme,
}) {
  const t = useT();
  const isOwner = user.role === "owner";
  const [tab, setTab] = useState(
    autoScanFile ? "scan" : returnedFromCheckout ? "buy" : isOwner ? "scan" : "expenses",
  );
  const [showPurchaseBanner, setShowPurchaseBanner] = useState(
    !!purchaseJustCompleted,
  );
  const [categories, setCategories] = useState([]);
  const [mileageRate, setMileageRate] = useState(0.54);
  const [settings, setSettings] = useState({
    company_name: "My Business",
    mileage_rate: 0.54,
    mileage_unit: "mile",
    submission_email: "",
  });
  const [reports, setReports] = useState([]);
  const [expensesFilter, setExpensesFilter] = useState("all"); // "all" | report_id
  const [creditBalance, setCreditBalance] = useState(null); // millicredits

  const openReport = (id) => {
    setExpensesFilter(id);
    setTab("expenses");
  };

  const reloadReports = () =>
    api
      .listReports()
      .then(setReports)
      .catch(() => {});

  const reloadHealth = () =>
    api
      .health()
      .then((h) => {
        setCategories(h.categories || []);
        if (h.mileageRate) setMileageRate(h.mileageRate);
        if (h.settings) setSettings(h.settings);
        if (h.creditBalance !== undefined) setCreditBalance(h.creditBalance);
      })
      .catch(() => {});

  useEffect(() => {
    reloadHealth();
    reloadReports();
  }, []);

  return (
    <div className="app">
      <header className="app-header">
        <BrandLockup />
        <div className="header-actions">
          <LanguageSwitcher />
          {isOwner && creditBalance !== null && (
            <button
              className={`credit-badge${creditBalance > 0 && creditBalance < LOW_BALANCE_THRESHOLD ? " low" : ""}`}
              onClick={() => setTab("buy")}
              title={
                creditBalance < LOW_BALANCE_THRESHOLD
                  ? t("app.header.scansLowTooltip")
                  : t("app.header.scansTooltip")
              }
            >
              <ScanIcon className="icon" />{" "}
              {Math.floor(creditBalance / MILLICREDITS_PER_SCAN)} {t("landing.scansSuffix")}
            </button>
          )}
          <button
            className="theme-toggle"
            onClick={() => setTheme(nextTheme)}
            title={t("app.header.themeTooltip", { theme: THEME_LABEL[theme] })}
          >
            {theme === "system" ? (
              <MonitorIcon className="icon" />
            ) : theme === "dark" ? (
              <MoonIcon className="icon" />
            ) : (
              <SunIcon className="icon" />
            )}
          </button>
          <button
            className="theme-toggle"
            onClick={onLoggedOut}
            title={t("app.header.signOutTooltip")}
          >
            <LogoutIcon className="icon" />
          </button>
        </div>
      </header>

      <nav className="tabs">
        {[
          ...(isOwner ? [["scan", t("app.nav.scan")]] : []),
          ["expenses", t("app.nav.expenses")],
          ["reports", t("app.nav.reports")],
          ...(isOwner ? [["settings", t("app.nav.settings")]] : []),
        ].map(([id, label]) => (
          <button
            key={id}
            className={tab === id ? "active" : ""}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </nav>

      <main>
        {tab === "scan" && isOwner && (
          <ScanView
            categories={categories}
            mileageRate={mileageRate}
            settings={settings}
            reports={reports}
            goToReports={() => setTab("reports")}
            onSaved={() => {
              setTab("expenses");
              reloadHealth();
            }}
            onInsufficientCredits={() => setTab("buy")}
            autoScanFile={autoScanFile}
            onAutoScanHandled={onAutoScanHandled}
          />
        )}
        {tab === "expenses" && (
          <ExpensesView
            categories={categories}
            settings={settings}
            reports={reports}
            goToReports={() => setTab("reports")}
            filter={expensesFilter}
            setFilter={setExpensesFilter}
            readOnly={!isOwner}
          />
        )}
        {tab === "reports" && (
          <ReportsView
            reports={reports}
            reloadReports={reloadReports}
            onOpenReport={openReport}
            readOnly={!isOwner}
            defaultSubmissionEmail={settings.submission_email}
          />
        )}
        {tab === "buy" && isOwner && (
          <BuyCreditsView
            onBack={() => setTab("scan")}
            justPurchased={showPurchaseBanner}
            creditBalance={creditBalance}
            onDismissBanner={() => setShowPurchaseBanner(false)}
          />
        )}
        {tab === "settings" && isOwner && (
          <SettingsView
            user={user}
            creditBalance={creditBalance}
            onBuyCredits={() => setTab("buy")}
            onSaved={reloadHealth}
            onLoggedOut={onLoggedOut}
          />
        )}
      </main>
    </div>
  );
}

/* ---------------- Buy credits ---------------- */

function BuyCreditsView({ onBack, justPurchased, creditBalance, onDismissBanner }) {
  const t = useT();
  const [busy, setBusy] = useState(null); // packId being checked out
  const [error, setError] = useState("");
  const [currency, setCurrency] = useState("usd");

  // detectCurrency reads the browser locale (client-only); pick the neutral
  // default on first render, then switch once mounted.
  useEffect(() => {
    setCurrency(detectCurrency());
  }, []);

  async function buy(packId) {
    setError("");
    setBusy(packId);
    try {
      const { checkoutUrl } = await api.checkout(packId, currency);
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  }

  return (
    <section className="card">
      <h3>{t("app.buy.title")}</h3>
      {justPurchased && (
        <div className="success-banner">
          <span>
            <CheckCircleIcon className="icon" />{" "}
            {t("app.buy.paymentSuccess", {
              balance:
                creditBalance !== null
                  ? Math.floor(creditBalance / MILLICREDITS_PER_SCAN)
                  : "…",
            })}
          </span>
          <button className="link-btn" onClick={onDismissBanner}>
            <XIcon className="icon" />
          </button>
        </div>
      )}
      <p className="muted">{t("app.buy.sub")}</p>
      <div className="currency-toggle">
        {CURRENCIES.map((c) => (
          <button
            key={c.code}
            type="button"
            className={c.code === currency ? "active" : ""}
            disabled={busy !== null}
            onClick={() => setCurrency(c.code)}
          >
            {c.label}
          </button>
        ))}
      </div>
      {error && <div className="error">{error}</div>}
      <div className="pack-grid">
        {PACKS.map((p) => (
          <button
            key={p.id}
            className="pack-card"
            disabled={busy !== null}
            onClick={() => buy(p.id)}
          >
            {p.badge && <span className="pack-badge">{p.badge}</span>}
            <strong>{p.label}</strong>
            <span className="pack-credits">{t("app.buy.packScans", { count: p.scans })}</span>
            <span className="pack-price">{formatPrice(p, currency)}</span>
            <span className="pack-credits">
              {formatPerScan(p, currency)}{t("landing.perScanSuffix")}
            </span>
            {busy === p.id && <span>{t("app.buy.redirecting")}</span>}
          </button>
        ))}
      </div>
      <button className="secondary" onClick={onBack}>
        {t("app.back")}
      </button>
    </section>
  );
}

/* ---------------- Settings ---------------- */

function SettingsView({
  user,
  creditBalance,
  onBuyCredits,
  onSaved,
  onLoggedOut,
}) {
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState(""); // '', 'saving', 'saved'
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .getSettings()
      .then(setForm)
      .catch((err) => setError(err.message));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save(e) {
    e.preventDefault();
    setError("");
    setStatus("saving");
    try {
      const updated = await api.saveSettings(form);
      setForm(updated);
      setStatus("saved");
      onSaved?.();
    } catch (err) {
      setError(err.message);
      setStatus("");
    }
  }

  return (
    <section className="card">
      <h3>Account</h3>
      {error && <div className="error">{error}</div>}

      <div className="settings-account-row">
        <div>
          <span className="muted">Signed in as</span>
          <p className="settings-email">{user.email}</p>
        </div>
        <div className="settings-balance">
          <span className="muted">Scan balance</span>
          <p className="settings-email">
            {creditBalance !== null
              ? Math.floor(creditBalance / MILLICREDITS_PER_SCAN)
              : "…"}{" "}
            scans
          </p>
          <button type="button" className="secondary" onClick={onBuyCredits}>
            Buy more
          </button>
        </div>
      </div>

      <h3>Report settings</h3>
      {!form ? (
        <p className="muted">Loading…</p>
      ) : (
        <form onSubmit={save}>
          <Field label="Company name">
            <input value={form.company_name} onChange={set("company_name")} />
          </Field>
          <Field label="Default submission email">
            <input
              type="email"
              multiple
              placeholder="bookkeeper@example.com, cfo@example.com"
              value={form.submission_email || ""}
              onChange={set("submission_email")}
            />
          </Field>
          <p className="muted" style={{ fontSize: 12, margin: "-4px 0 8px" }}>
            Pre-fills the recipient when emailing a report from the Reports
            tab — editable per-report, so this is just the default. Separate
            multiple addresses with commas.
          </p>
          <div className="row">
            <Field
              label={`Mileage rate (currency per ${form.mileage_unit === "km" ? "km" : "mile"})`}
            >
              <input
                type="number"
                step="0.01"
                value={form.mileage_rate}
                onChange={set("mileage_rate")}
              />
            </Field>
            <Field label="Measurement">
              <select
                value={form.mileage_unit || "mile"}
                onChange={set("mileage_unit")}
              >
                <option value="mile">Miles</option>
                <option value="km">Kilometers</option>
              </select>
            </Field>
          </div>
          <button className="primary" disabled={status === "saving"}>
            {status === "saving" ? "Saving…" : "Save settings"}
          </button>
          {status === "saved" && <span className="muted"> Saved.</span>}
        </form>
      )}

      <CategoriesSection onChanged={onSaved} />

      <BookkeepersSection />

      <ScanHistorySection />

      <PurchaseHistorySection />

      <DangerZone onLoggedOut={onLoggedOut} />
    </section>
  );
}

function CategoriesSection({ onChanged }) {
  const [categories, setCategories] = useState(null); // [{id, name}]
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reload = () =>
    api
      .listCategories()
      .then(setCategories)
      .catch((err) => setError(err.message));

  // NOT useEffect(reload, []) — reload() returns a Promise, and React
  // would try to call that Promise as the effect's cleanup function on
  // unmount. Dev builds just warn about this; production builds skip the
  // typeof guard and crash ("x is not a function") the moment this
  // component unmounts — i.e. the instant you navigate to another tab.
  useEffect(() => {
    reload();
  }, []);

  async function add(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.addCategory(newName);
      setNewName("");
      reload();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  function startEdit(c) {
    setEditingId(c.id);
    setEditingName(c.name);
    setError("");
  }

  async function saveEdit(id) {
    setError("");
    try {
      await api.renameCategory(id, editingName);
      setEditingId(null);
      reload();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    }
  }

  async function remove(id) {
    if (!confirm("Delete this category? Existing expenses keep it as text.")) return;
    setError("");
    try {
      await api.deleteCategory(id);
      reload();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <>
      <h3>Categories</h3>
      <p className="muted">
        Shown in the category picker when scanning or editing an expense.
        Deleting one doesn't change expenses that already used it.
      </p>
      {error && <div className="error">{error}</div>}
      {categories?.length > 0 && (
        <ul className="member-list">
          {categories.map((c) => (
            <li key={c.id}>
              {editingId === c.id ? (
                <form
                  className="row"
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveEdit(c.id);
                  }}
                >
                  <input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    autoFocus
                    required
                  />
                  <button className="secondary">Save</button>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => setEditingId(null)}
                  >
                    Cancel
                  </button>
                </form>
              ) : (
                <>
                  <span>{c.name}</span>
                  <span className="member-actions">
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => startEdit(c)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => remove(c.id)}
                    >
                      Delete
                    </button>
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="row">
        <input
          placeholder="New category name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          required
        />
        <button className="secondary" disabled={busy}>
          {busy ? "Adding…" : "Add"}
        </button>
      </form>
    </>
  );
}

function BookkeepersSection() {
  const [members, setMembers] = useState(null);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reload = () =>
    api
      .listMembers()
      .then(setMembers)
      .catch((err) => setError(err.message));

  // NOT useEffect(reload, []) — reload() returns a Promise, and React
  // would try to call that Promise as the effect's cleanup function on
  // unmount. Dev builds just warn about this; production builds skip the
  // typeof guard and crash ("x is not a function") the moment this
  // component unmounts — i.e. the instant you navigate to another tab.
  useEffect(() => {
    reload();
  }, []);

  async function invite(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.inviteMember(email, message);
      setEmail("");
      setMessage("");
      reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    if (!confirm("Remove this person's shared access?")) return;
    try {
      await api.removeMember(id);
      reload();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <>
      <h3>Shared access</h3>
      <p className="muted">
        Read-only access — can view expenses, receipts and reports, but can't
        scan, edit, or delete anything.
      </p>
      {error && <div className="error">{error}</div>}
      {members?.length > 0 && (
        <ul className="member-list">
          {members.map((m) => (
            <li key={m.id}>
              <span>{m.email}</span>
              <button
                type="button"
                className="link-btn"
                onClick={() => remove(m.id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={invite} className="stack">
        <input
          type="email"
          placeholder="accountant@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <textarea
          placeholder="Add a message (optional)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={1000}
          rows={2}
        />
        <button className="secondary" disabled={busy}>
          {busy ? "Inviting…" : "Invite"}
        </button>
      </form>
    </>
  );
}

function PurchaseHistorySection() {
  const [transactions, setTransactions] = useState(null);

  useEffect(() => {
    api
      .listTransactions()
      .then(setTransactions)
      .catch(() => {});
  }, []);

  if (!transactions?.length) return null;

  return (
    <>
      <h3>Purchase history</h3>
      <ul className="member-list">
        {transactions.map((t) => (
          <li key={t.payment_id}>
            <span>
              {t.pack_id} —{" "}
              {Math.floor(t.millicredits / MILLICREDITS_PER_SCAN)} scans —{" "}
              {t.currency.toUpperCase()} {t.amount}
            </span>
            <span className={`badge ${t.status === "paid" ? "assigned" : "unassigned"}`}>
              {t.status}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

function ScanHistorySection() {
  const [history, setHistory] = useState(null);

  useEffect(() => {
    api
      .scanHistory()
      .then(setHistory)
      .catch(() => {});
  }, []);

  if (!history?.totalScans) return null;

  return (
    <>
      <h3>Scan usage</h3>
      <div className="scan-stat-row">
        <div className="scan-stat">
          <span className="muted">Today</span>
          <strong>{history.todayScans}</strong>
        </div>
        <div className="scan-stat">
          <span className="muted">This week</span>
          <strong>{history.weekScans}</strong>
        </div>
        <div className="scan-stat">
          <span className="muted">This month</span>
          <strong>{history.monthScans}</strong>
        </div>
        <div className="scan-stat">
          <span className="muted">All time</span>
          <strong>{history.totalScans}</strong>
        </div>
      </div>
      <ul className="member-list">
        {history.rows.slice(0, 10).map((r) => (
          <li key={r.request_id}>
            <span>{new Date(r.created_at).toLocaleString()}</span>
            <span className="muted">
              {Math.floor(r.credits_charged / MILLICREDITS_PER_SCAN)} scan
              {r.credits_charged !== MILLICREDITS_PER_SCAN ? "s" : ""}
            </span>
            {r.imagePath ? (
              <a
                className="link-btn"
                href={`/uploads/${r.imagePath}`}
                target="_blank"
                rel="noreferrer"
              >
                View scan
              </a>
            ) : (
              <span className="muted">—</span>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

function DangerZone({ onLoggedOut }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function deleteAccount() {
    setBusy(true);
    setError("");
    try {
      await api.deleteAccount();
      onLoggedOut?.();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <>
      <h3>Danger zone</h3>
      {error && <div className="error">{error}</div>}
      {!confirming ? (
        <button type="button" className="secondary danger" onClick={() => setConfirming(true)}>
          Delete account
        </button>
      ) : (
        <div className="danger-confirm">
          <p>
            This permanently deletes your expenses, receipts, reports, and
            shared access. This can't be undone.
          </p>
          <button type="button" className="secondary" onClick={() => setConfirming(false)} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="danger" onClick={deleteAccount} disabled={busy}>
            {busy ? "Deleting…" : "Yes, delete everything"}
          </button>
        </div>
      )}
    </>
  );
}

/* ---------------- Scan ---------------- */

function ScanView({
  categories,
  mileageRate,
  settings,
  reports,
  goToReports,
  onSaved,
  onInsufficientCredits,
  autoScanFile,
  onAutoScanHandled,
}) {
  const t = useT();
  const [status, setStatus] = useState(""); // '', 'scanning', 'review', 'saving', 'drive'
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState(null);
  const [scanCtrl, setScanCtrl] = useState(null); // AbortController for the OCR
  const [dragging, setDragging] = useState(false);

  // A file picked from the landing page before login — scan it immediately
  // on arrival, exactly as if the user had just used the camera/file button.
  useEffect(() => {
    if (autoScanFile) {
      handleFile(autoScanFile);
      onAutoScanHandled?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScanFile]);

  async function handleFile(file) {
    if (!file) return;
    setError("");
    // Non-image uploads (PDF/DOCX) can't be shown via <img> until the server
    // converts them — skip the preview rather than showing a broken icon.
    setPreview(file.type.startsWith("image/") ? URL.createObjectURL(file) : null);
    setStatus("scanning");
    const ctrl = new AbortController();
    setScanCtrl(ctrl);
    try {
      const ocr = await api.scanReceipt(file, ctrl.signal);
      setPreview(`/uploads/${ocr.image_path}`);
      setForm({
        report_id: defaultReportId(reports),
        vendor: ocr.vendor || "",
        expense_date: ocr.expense_date || "",
        amount: ocr.amount || "",
        currency: ocr.currency || "EUR",
        vat_amount: ocr.vat_amount || "",
        category: ocr.category || "Other",
        description: ocr.description || "",
        tag: "",
        notes: "",
        image_path: ocr.image_path,
        raw_ocr: ocr,
        confidence: ocr.confidence,
      });
      setStatus("review");
    } catch (err) {
      if (err.name === "AbortError") setStatus("");
      else if (err.status === 402) {
        setStatus("");
        onInsufficientCredits();
      } else {
        setError(err.message);
        setStatus("");
      }
    } finally {
      setScanCtrl(null);
    }
  }

  function onDragOver(e) {
    e.preventDefault();
    setDragging(true);
  }
  function onDragLeave() {
    setDragging(false);
  }
  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  }

  function stopScan() {
    scanCtrl?.abort();
    setPreview(null);
    setStatus("");
  }

  async function save() {
    setStatus("saving");
    try {
      await api.createExpense(form);
      reset();
      onSaved();
    } catch (err) {
      setError(err.message);
      setStatus("review");
    }
  }

  function reset() {
    setForm(null);
    setPreview(null);
    setStatus("");
    setError("");
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section className="card">
      {error && <div className="error">{error}</div>}

      {status === "" && (
        <div className="scan-options">
          <label className="dropzone">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => handleFile(e.target.files?.[0])}
              hidden
            />
            <div className="dz-inner">
              <div className="dz-icon">
                <CameraIcon className="icon" />
              </div>
              <strong>{t("app.scan.takePhoto")}</strong>
              <span>{t("app.scan.useCamera")}</span>
            </div>
          </label>
          <label
            className={`dropzone${dragging ? " dropzone-active" : ""}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <input
              type="file"
              accept="image/*,application/pdf,.docx"
              onChange={(e) => handleFile(e.target.files?.[0])}
              hidden
            />
            <div className="dz-inner">
              <div className="dz-icon">
                <FolderIcon className="icon" />
              </div>
              <strong>{t("app.scan.chooseFile")}</strong>
              <span>
                {dragging
                  ? t("app.scan.dropToUpload")
                  : t("app.scan.fileTypesHint")}
              </span>
            </div>
          </label>
        </div>
      )}

      {status === "" && (
        <button className="drive-btn" onClick={() => setStatus("batch")}>
          <FolderIcon className="icon" /> {t("app.scan.batchScan")}
        </button>
      )}

      {status === "" && (
        <button className="drive-btn" onClick={() => setStatus("drive")}>
          <CarIcon className="icon" /> {t("app.scan.logDrive")}
        </button>
      )}

      {status === "" && (
        <p className="scan-alt-methods">
          {t("app.scan.altMethodsPrefix")}{" "}
          <a href="mailto:receipts@freeainow.io">receipts@freeainow.io</a>{" "}
          {t("app.scan.altMethodsSuffix")}
        </p>
      )}

      {status === "batch" && (
        <BatchScan
          categories={categories}
          reports={reports}
          goToReports={goToReports}
          onCancel={reset}
          onSaved={() => {
            reset();
            onSaved();
          }}
          onInsufficientCredits={onInsufficientCredits}
        />
      )}

      {status === "drive" && (
        <DriveForm
          mileageRate={mileageRate}
          mileageUnit={settings.mileage_unit}
          reports={reports}
          goToReports={goToReports}
          onCancel={reset}
          onSaved={() => {
            reset();
            onSaved();
          }}
        />
      )}

      {status === "scanning" && (
        <div className="scanning">
          {preview && <img src={preview} alt="receipt" className="preview" />}
          <p className="spinner-text">{t("app.scan.reading")}</p>
          <button className="secondary" onClick={stopScan}>
            <XIcon className="icon" /> {t("app.scan.stop")}
          </button>
        </div>
      )}

      {(status === "review" || status === "saving") && form && (
        <div className="review">
          {preview && <img src={preview} alt="receipt" className="preview" />}
          {form.confidence !== undefined && form.confidence < 0.6 && (
            <div className="warn">{t("app.scan.lowConfidence")}</div>
          )}

          <ReportPicker
            value={form.report_id}
            onChange={(v) => setForm((f) => ({ ...f, report_id: v }))}
            reports={reports}
            onCreateNew={goToReports}
          />

          <Field label={t("app.fieldMerchant")}>
            <input value={form.vendor} onChange={set("vendor")} />
          </Field>
          <div className="row">
            <Field label={t("app.fieldDate")}>
              <input
                type="date"
                value={form.expense_date}
                onChange={set("expense_date")}
              />
            </Field>
            <Field label={t("app.fieldCategory")}>
              <select value={form.category} onChange={set("category")}>
                {categories.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="row">
            <Field label={t("app.fieldTotal")}>
              <input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={set("amount")}
              />
            </Field>
            <Field label={t("app.fieldCurrency")}>
              <input value={form.currency} onChange={set("currency")} />
            </Field>
          </div>
          <Field label={t("app.fieldVatAmount")}>
            <input
              type="number"
              step="0.01"
              value={form.vat_amount}
              onChange={set("vat_amount")}
            />
          </Field>
          <Field label={t("app.fieldDescription")}>
            <input value={form.description} onChange={set("description")} />
          </Field>
          <Field label={t("app.fieldTag")}>
            <input
              value={form.tag}
              onChange={set("tag")}
              placeholder={t("app.fieldTagPlaceholder")}
            />
          </Field>
          <Field label={t("app.fieldNotes")}>
            <input value={form.notes} onChange={set("notes")} />
          </Field>

          <div className="actions">
            <button
              className="secondary"
              onClick={reset}
              disabled={status === "saving"}
            >
              {t("app.discard")}
            </button>
            <button
              className="primary"
              onClick={save}
              disabled={status === "saving"}
            >
              {status === "saving" ? t("app.saving") : t("app.scan.saveExpense")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------------- Batch scan ---------------- */

function BatchScan({
  categories,
  reports,
  goToReports,
  onCancel,
  onSaved,
  onInsufficientCredits,
}) {
  const t = useT();
  const [reportId, setReportId] = useState(defaultReportId(reports));
  const [items, setItems] = useState([]); // {image_path, vendor, expense_date, amount, currency, category, description, previewUrl}
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setError("");
    setScanning(true);
    setProgress({ done: 0, total: files.length });
    const collected = [];
    let outOfCredits = false;
    for (let i = 0; i < files.length; i++) {
      try {
        const ocr = await api.scanReceipt(files[i]);
        collected.push({
          image_path: ocr.image_path,
          vendor: ocr.vendor || "",
          expense_date: ocr.expense_date || "",
          amount: ocr.amount || "",
          currency: ocr.currency || "EUR",
          vat_amount: ocr.vat_amount || "",
          category: ocr.category || "Other",
          description: ocr.description || "",
          tag: ocr.tag || "",
          previewUrl: `/uploads/${ocr.image_path}`,
        });
      } catch (err) {
        if (err.status === 402) {
          outOfCredits = true;
          setProgress({ done: i, total: files.length });
          break;
        }
        collected.push({ error: err.message, fileName: files[i].name });
      }
      setProgress({ done: i + 1, total: files.length });
    }
    setItems((prev) => [...prev, ...collected.filter((c) => !c.error)]);
    const failed = collected.filter((c) => c.error);
    if (failed.length) setError(t("app.batch.failedToScan", { count: failed.length }));
    setScanning(false);
    if (outOfCredits) onInsufficientCredits();
  }

  const setItem = (idx, key) => (e) =>
    setItems((arr) =>
      arr.map((it, i) => (i === idx ? { ...it, [key]: e.target.value } : it)),
    );

  const removeItem = (idx) =>
    setItems((arr) => arr.filter((_, i) => i !== idx));

  const total = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);

  async function saveAll() {
    if (!items.length) return;
    setSaving(true);
    setError("");
    try {
      for (const it of items) {
        await api.createExpense({
          report_id: reportId,
          vendor: it.vendor,
          expense_date: it.expense_date,
          amount: Number(it.amount) || 0,
          currency: it.currency,
          vat_amount: Number(it.vat_amount) || 0,
          category: it.category,
          description: it.description,
          tag: it.tag,
          image_path: it.image_path,
        });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="batch">
      {error && <div className="error">{error}</div>}

      <ReportPicker
        value={reportId}
        onChange={setReportId}
        reports={reports}
        onCreateNew={goToReports}
      />

      <label className="dropzone">
        <input
          type="file"
          accept="image/*,application/pdf,.docx"
          multiple
          onChange={handleFiles}
          hidden
          disabled={scanning}
        />
        <div className="dz-inner">
          <div className="dz-icon">
            <FolderIcon className="icon" />
          </div>
          <strong>{scanning ? t("app.batch.scanning") : t("app.batch.chooseReceipts")}</strong>
          <span>
            {scanning
              ? t("app.batch.readingProgress", { done: progress.done, total: progress.total })
              : t("app.batch.selectMultiple")}
          </span>
        </div>
      </label>

      {items.length > 0 && (
        <>
          <div className="batch-count">
            {items.length} receipts · €{total.toFixed(2)}
          </div>
          <ul className="batch-list">
            {items.map((it, idx) => (
              <li key={idx} className="batch-item">
                {it.previewUrl && (
                  <img className="thumb" src={it.previewUrl} alt="" />
                )}
                <div className="batch-fields">
                  <Field label={t("app.fieldMerchant")}>
                    <input
                      value={it.vendor}
                      onChange={setItem(idx, "vendor")}
                    />
                  </Field>
                  <div className="row">
                    <Field label={t("app.fieldDate")}>
                      <input
                        type="date"
                        value={it.expense_date}
                        onChange={setItem(idx, "expense_date")}
                      />
                    </Field>
                    <Field label={t("app.fieldTotal")}>
                      <input
                        type="number"
                        step="0.01"
                        value={it.amount}
                        onChange={setItem(idx, "amount")}
                      />
                    </Field>
                    <Field label={t("app.fieldVatAmount")}>
                      <input
                        type="number"
                        step="0.01"
                        value={it.vat_amount}
                        onChange={setItem(idx, "vat_amount")}
                      />
                    </Field>
                  </div>
                  <Field label={t("app.fieldCategory")}>
                    <select
                      value={it.category}
                      onChange={setItem(idx, "category")}
                    >
                      {categories.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t("app.fieldDescription")}>
                    <input
                      value={it.description}
                      onChange={setItem(idx, "description")}
                    />
                  </Field>
                  <Field label={t("app.fieldTag")}>
                    <input
                      value={it.tag}
                      onChange={setItem(idx, "tag")}
                      placeholder={t("app.fieldTagPlaceholder")}
                    />
                  </Field>
                </div>
                <button className="icon-btn" onClick={() => removeItem(idx)}>
                  <XIcon className="icon" />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="actions">
        <button className="secondary" onClick={onCancel} disabled={saving}>
          {t("app.cancel")}
        </button>
        <button
          className="primary"
          onClick={saveAll}
          disabled={saving || scanning || items.length === 0}
        >
          {saving ? t("app.saving") : t("app.batch.saveExpenses", { count: items.length || "" })}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Drive / mileage ---------------- */

// Debounced address autocomplete input. Calls onPick with {label, lat, lon}
// when a suggestion is chosen; keeps free-typed text in sync via onText.
function AddressInput({ label, value, onText, onPick, place }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const q = value?.trim() || "";
    if (q.length < 3) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const s = await api.geocode(q);
        setSuggestions(s);
        setOpen(true);
      } catch {
        setSuggestions([]);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <div className="field autocomplete">
      <span>{label}</span>
      <input
        value={value}
        placeholder={place}
        autoComplete="off"
        onChange={(e) => {
          onText(e.target.value);
          onPick(null); // typing invalidates a previously picked coordinate
        }}
        onFocus={() => suggestions.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && suggestions.length > 0 && (
        <ul className="suggestions">
          {suggestions.map((s, i) => (
            <li
              key={i}
              onMouseDown={() => {
                onText(s.label);
                onPick(s);
                setOpen(false);
              }}
            >
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function mapLink(coord, text) {
  const q = coord
    ? `${coord.lat},${coord.lon}`
    : encodeURIComponent(text || "");
  if (!q) return null;
  return `https://www.openstreetmap.org/search?query=${q}`;
}

const STOP_LETTERS = "ABCDEFGHIJKLMN";
const stopCaption = (i, n) => (i === 0 ? "from" : i === n - 1 ? "to" : "via");

// Leaflet map modal: tap the map to drop a pin; reverse-geocodes to a label.
function MapPicker({ initial, onPick, onClose }) {
  const ref = useRef(null);
  const [coord, setCoord] = useState(initial || null);
  const [label, setLabel] = useState("");

  useEffect(() => {
    const start = initial || { lat: 52.1, lon: 5.1 };
    const map = L.map(ref.current).setView(
      [start.lat, start.lon],
      initial ? 13 : 7,
    );
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "© OpenStreetMap",
    }).addTo(map);
    let marker = initial
      ? L.circleMarker([initial.lat, initial.lon], mk).addTo(map)
      : null;
    map.on("click", async (e) => {
      const c = { lat: e.latlng.lat, lon: e.latlng.lng };
      if (marker) marker.setLatLng([c.lat, c.lon]);
      else marker = L.circleMarker([c.lat, c.lon], mk).addTo(map);
      setCoord(c);
      setLabel("…");
      try {
        const r = await api.reverseGeocode(c.lat, c.lon);
        setLabel(r.label);
      } catch {
        setLabel(`${c.lat.toFixed(5)}, ${c.lon.toFixed(5)}`);
      }
    });
    // Leaflet needs a size recalc once the modal is laid out.
    setTimeout(() => map.invalidateSize(), 60);
    return () => map.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mk = {
    radius: 8,
    color: "#fff",
    weight: 2,
    fillColor: "#0d9488",
    fillOpacity: 1,
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal map-picker" onClick={(e) => e.stopPropagation()}>
        <h3>Drop a pin</h3>
        <div ref={ref} className="picker-map" />
        <div className="picker-label">
          {label || "Tap the map to set the location"}
        </div>
        <div className="actions">
          <button className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="primary"
            disabled={!coord}
            onClick={() => onPick({ label: label || "", coord })}
          >
            Use this location
          </button>
        </div>
      </div>
    </div>
  );
}

const MILES_PER_KM = 0.6213711922;

function DriveForm({ mileageRate, mileageUnit, reports, goToReports, onCancel, onSaved }) {
  const [unit, setUnit] = useState(mileageUnit === "km" ? "km" : "mile");
  const unitLabel = unit === "km" ? "km" : "mi";
  const [manual, setManual] = useState(false);
  const [stops, setStops] = useState([
    { label: "", coord: null },
    { label: "", coord: null },
  ]);
  const [miles, setMiles] = useState("");
  const [rate, setRate] = useState(mileageRate);

  function changeUnit(next) {
    if (next === unit) return;
    // Distance and rate are entered in whatever unit is picked — convert
    // both when the unit changes so the total (distance × rate) stays the
    // same instead of silently changing when the label flips.
    const distanceFactor = next === "km" ? 1 / MILES_PER_KM : MILES_PER_KM;
    const rateFactor = next === "km" ? MILES_PER_KM : 1 / MILES_PER_KM;
    setMiles((m) => (m === "" ? m : Number((Number(m) * distanceFactor).toFixed(2))));
    setRate((r) => (r === "" ? r : Number((Number(r) * rateFactor).toFixed(3))));
    setUnit(next);
  }
  const [returnTrip, setReturnTrip] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reportId, setReportId] = useState(defaultReportId(reports));
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [picking, setPicking] = useState(null); // stop index being pinned

  const amount = (Number(miles) || 0) * (Number(rate) || 0);

  const setStop = (i, patch) =>
    setStops((arr) => arr.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const addStop = () => setStops((arr) => [...arr, { label: "", coord: null }]);
  const removeStop = (i) =>
    setStops((arr) => (arr.length > 2 ? arr.filter((_, j) => j !== i) : arr));

  async function calc() {
    const filled = stops.filter((s) => s.label.trim());
    if (filled.length < 2) {
      setError("Enter at least two stops.");
      return;
    }
    setError("");
    setCalculating(true);
    try {
      const r = await api.calcRoute(filled, returnTrip);
      setMiles(unit === "km" ? r.km : r.miles);
      if (r.stops)
        setStops(r.stops.map((s) => ({ label: s.label, coord: s.coord })));
    } catch (err) {
      setError(err.message);
    } finally {
      setCalculating(false);
    }
  }

  async function save() {
    const filled = stops.filter((s) => s.label.trim());
    if (filled.length < 2 || !miles) {
      setError("Add stops and a distance (use Calculate or type it).");
      return;
    }
    setSaving(true);
    try {
      await api.saveDrive({
        report_id: reportId,
        stops: filled,
        distance: Number(miles),
        rate: Number(rate),
        unit,
        returnTrip,
        date,
      });
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="drive">
      {error && <div className="error">{error}</div>}

      <ReportPicker
        value={reportId}
        onChange={setReportId}
        reports={reports}
        onCreateNew={goToReports}
      />

      <label className="mode-toggle">
        <input
          type="checkbox"
          checked={manual}
          onChange={(e) => setManual(e.target.checked)}
        />
        <span>Manual entry (type distance myself, no lookup)</span>
      </label>

      {stops.map((s, i) => (
        <div key={i} className="stop-row">
          {manual ? (
            <Field
              label={`${STOP_LETTERS[i]} — ${stopCaption(i, stops.length)}`}
            >
              <input
                value={s.label}
                onChange={(e) => setStop(i, { label: e.target.value })}
                placeholder="Location"
              />
            </Field>
          ) : (
            <AddressInput
              label={`${STOP_LETTERS[i]} — ${stopCaption(i, stops.length)}`}
              value={s.label}
              onText={(t) => setStop(i, { label: t, coord: null })}
              onPick={(p) =>
                p
                  ? setStop(i, {
                      label: p.label,
                      coord: { lat: p.lat, lon: p.lon },
                    })
                  : setStop(i, { coord: null })
              }
              place="Start typing an address…"
            />
          )}
          <div className="stop-btns">
            {!manual && (
              <button
                className="pin-btn"
                title="Drop a pin on the map"
                onClick={() => setPicking(i)}
              >
                <PinIcon className="icon" />
              </button>
            )}
            {stops.length > 2 && (
              <button
                className="icon-btn"
                title="Remove stop"
                onClick={() => removeStop(i)}
              >
                <XIcon className="icon" />
              </button>
            )}
          </div>
        </div>
      ))}

      <button className="secondary calc" onClick={addStop}>
        ＋ Add stop
      </button>

      {!manual && (
        <button
          className="secondary calc"
          onClick={calc}
          disabled={calculating}
        >
          {calculating ? (
            "Calculating…"
          ) : (
            <>
              <RulerIcon className="icon" /> Calculate driving distance
            </>
          )}
        </button>
      )}

      <label className="return-toggle">
        <input
          type="checkbox"
          checked={returnTrip}
          onChange={(e) => setReturnTrip(e.target.checked)}
        />
        <span>Return trip (back to start)</span>
      </label>

      <div className="row">
        <Field label="Total distance">
          <div className="unit-input">
            <input
              type="number"
              step="0.1"
              value={miles}
              onChange={(e) => setMiles(e.target.value)}
            />
            <select value={unit} onChange={(e) => changeUnit(e.target.value)}>
              <option value="mile">Miles</option>
              <option value="km">Kilometres</option>
            </select>
          </div>
        </Field>
        <Field label="Rate">
          <div className="unit-input">
            <input
              type="number"
              step="0.01"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
            <span className="unit-input-suffix">€/{unitLabel}</span>
          </div>
        </Field>
        <Field label="Date">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
      </div>

      <div className="drive-total">
        {miles || 0} {unitLabel} × €{Number(rate || 0).toFixed(2)} ={" "}
        <strong>€{amount.toFixed(2)}</strong>
        {returnTrip && <span className="muted"> (incl. return)</span>}
      </div>

      <div className="actions">
        <button className="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button className="primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save drive"}
        </button>
      </div>

      {picking !== null && (
        <MapPicker
          initial={stops[picking]?.coord}
          onClose={() => setPicking(null)}
          onPick={({ label, coord }) => {
            setStop(picking, { label, coord });
            setPicking(null);
          }}
        />
      )}
    </div>
  );
}

/* ---------------- Expenses ---------------- */

// Optional expense-list columns a user can show/hide. Amount and the receipt
// thumbnail are never included here — they always stay visible.
// "reportStatus" is the linked report's actual status (live/submitted/
// approved/paid); "report" is the linked report's name; "tag" is a free-text
// label the user sets themselves (empty until set — independent of report
// assignment, not derived from OCR).
const OPTIONAL_COLUMNS = [
  { key: "date", label: "Date" },
  { key: "report", label: "Report" },
  { key: "reportStatus", label: "Status" },
  { key: "merchant", label: "Merchant" },
  { key: "description", label: "Description" },
  { key: "category", label: "Category" },
  { key: "tag", label: "Tag" },
];
const COLUMNS_STORAGE_KEY = "expenseColumns";

function loadColumns() {
  try {
    const saved = JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY));
    if (saved) return { ...defaultColumns(), ...saved };
  } catch {
    /* ignore bad JSON */
  }
  return defaultColumns();
}

function defaultColumns() {
  return Object.fromEntries(OPTIONAL_COLUMNS.map((c) => [c.key, true]));
}

function ExpensesView({
  categories,
  reports,
  goToReports,
  filter,
  setFilter,
  readOnly,
}) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null); // image_path being previewed
  const [columns, setColumns] = useState(loadColumns);
  const [showColumns, setShowColumns] = useState(false);
  // FX-converted total for the current filter — a naive sum of `amount` is
  // only correct when every expense shares one currency (see the backend's
  // /api/expenses/total, which converts each into one target currency the
  // same way the PDF report generator does).
  const [totalInfo, setTotalInfo] = useState({ total: 0, currency: "EUR" });
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [receiptFilter, setReceiptFilter] = useState("all");
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [groupByTag, setGroupByTag] = useState(false);
  const [showArchivedExpenses, setShowArchivedExpenses] = useState(false);

  useEffect(() => {
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columns));
  }, [columns]);

  const toggleColumn = (key) => setColumns((c) => ({ ...c, [key]: !c[key] }));

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  async function load() {
    setLoading(true);
    const params = filter === "all" ? {} : { report_id: filter };
    const [items, totals] = await Promise.all([
      api.listExpenses(params),
      api.expensesTotal(params).catch(() => ({ total: 0, currency: "EUR" })),
    ]);
    setExpenses(items);
    setTotalInfo(totals);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function remove(id) {
    if (!confirm("Delete this expense?")) return;
    await api.deleteExpense(id);
    load();
  }

  const [regenId, setRegenId] = useState(null);
  async function regen(id) {
    setRegenId(id);
    try {
      await api.regenReceipt(id);
      await load();
    } catch (err) {
      alert(err.message);
    } finally {
      setRegenId(null);
    }
  }

  // Text/date columns sort case-insensitively A→Z (or chronologically);
  // amount sorts numerically. Clicking the same header again flips direction.
  const SORT_ACCESSORS = {
    date: (e) => e.expense_date || "",
    reportStatus: (e) => e.report_status || "",
    merchant: (e) => (e.vendor || "").toLowerCase(),
    description: (e) => (e.description || "").toLowerCase(),
    category: (e) => (e.category || "").toLowerCase(),
    report: (e) => (e.report_title || "").toLowerCase(),
    tag: (e) => (e.tag || "").toLowerCase(),
    amount: (e) => e.amount || 0,
  };
  const sortedExpenses = [...expenses].sort((a, b) => {
    const va = SORT_ACCESSORS[sortKey]?.(a) ?? "";
    const vb = SORT_ACCESSORS[sortKey]?.(b) ?? "";
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // Flags likely-duplicate expenses (same vendor + same amount, any date) so
  // both copies can be highlighted in the list.
  const duplicateIds = (() => {
    const ids = new Set();
    const groups = new Map();
    for (const e of expenses) {
      const vendor = (e.vendor || "").trim().toLowerCase();
      if (!vendor) continue;
      const key = `${vendor}|${(Number(e.amount) || 0).toFixed(2)}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(e);
    }
    for (const group of groups.values()) {
      if (group.length < 2) continue;
      for (const e of group) ids.add(e.id);
    }
    return ids;
  })();

  // Distinct tags/currencies actually in use, for their filter dropdowns.
  const uniqueTags = [...new Set(expenses.map((e) => e.tag).filter(Boolean))].sort();
  const uniqueCurrencies = [
    ...new Set(expenses.map((e) => (e.currency || "EUR").toUpperCase())),
  ].sort();

  // Free-text search across every visible text field, optionally narrowed
  // by category, date range, amount range, status, tag, currency, and
  // receipt presence — "all"/blank on any of these leaves it unrestricted,
  // so search still covers everything by default and each filter narrows
  // it further.
  const q = search.trim().toLowerCase();
  const filteredExpenses = sortedExpenses.filter((e) => {
    if (categoryFilter !== "all" && e.category !== categoryFilter) return false;
    if (tagFilter !== "all" && (e.tag || "") !== tagFilter) return false;
    if (currencyFilter !== "all" && (e.currency || "EUR").toUpperCase() !== currencyFilter) return false;
    if (receiptFilter === "has" && !e.image_path) return false;
    if (receiptFilter === "missing" && e.image_path) return false;
    if (statusFilter !== "all") {
      const st = e.report_status || "unassigned";
      if (st !== statusFilter) return false;
    }
    if (dateFrom && (e.expense_date || "") < dateFrom) return false;
    if (dateTo && (e.expense_date || "") > dateTo) return false;
    const amt = Number(e.amount) || 0;
    if (amountMin !== "" && amt < Number(amountMin)) return false;
    if (amountMax !== "" && amt > Number(amountMax)) return false;
    if (!q) return true;
    return (
      (e.vendor || "").toLowerCase().includes(q) ||
      (e.description || "").toLowerCase().includes(q) ||
      (e.category || "").toLowerCase().includes(q) ||
      (e.tag || "").toLowerCase().includes(q) ||
      (e.report_title || "").toLowerCase().includes(q) ||
      (e.expense_date || "").toLowerCase().includes(q)
    );
  });

  // Archived-report expenses are pulled out of the main list into their own
  // collapsed section (mirroring ReportsView's active/archived split) so a
  // wound-down report's expenses don't clutter the everyday view. If the
  // user explicitly filtered the status dropdown to "archived", there's
  // nothing left to hide — everything shown already is archived.
  const mainExpenses =
    statusFilter === "archived"
      ? filteredExpenses
      : filteredExpenses.filter((e) => e.report_status !== "archived");
  const archivedExpenses =
    statusFilter === "archived"
      ? []
      : filteredExpenses.filter((e) => e.report_status === "archived");

  // Grouped by currency (rather than naively summed) since the filtered set
  // can mix currencies, unlike totalInfo which the server already converts
  // to one currency for the report-level total.
  const filterActive =
    Boolean(q) ||
    categoryFilter !== "all" ||
    tagFilter !== "all" ||
    currencyFilter !== "all" ||
    receiptFilter !== "all" ||
    statusFilter !== "all" ||
    Boolean(dateFrom) ||
    Boolean(dateTo) ||
    amountMin !== "" ||
    amountMax !== "";

  function clearFilters() {
    setSearch("");
    setCategoryFilter("all");
    setTagFilter("all");
    setCurrencyFilter("all");
    setReceiptFilter("all");
    setStatusFilter("all");
    setDateFrom("");
    setDateTo("");
    setAmountMin("");
    setAmountMax("");
  }
  const subtotalsByCurrency = filteredExpenses.reduce((acc, e) => {
    const cur = e.currency || "EUR";
    acc[cur] = (acc[cur] || 0) + (Number(e.amount) || 0);
    return acc;
  }, {});
  const subtotalText = Object.entries(subtotalsByCurrency)
    .map(([cur, amt]) => `${cur} ${amt.toFixed(2)}`)
    .join(" + ");

  const subtotalTextFor = (items) => {
    const byCur = items.reduce((acc, e) => {
      const cur = e.currency || "EUR";
      acc[cur] = (acc[cur] || 0) + (Number(e.amount) || 0);
      return acc;
    }, {});
    return Object.entries(byCur)
      .map(([cur, amt]) => `${cur} ${amt.toFixed(2)}`)
      .join(" + ");
  };

  // "No tag" always sorts last among tag names, everything else A→Z.
  const sortTagNames = (names) =>
    names.sort((a, b) => {
      if (a === "No tag") return b === "No tag" ? 0 : 1;
      if (b === "No tag") return -1;
      return a.localeCompare(b);
    });

  const byKey = (items, keyFn) => {
    const buckets = {};
    for (const e of items) (buckets[keyFn(e)] ||= []).push(e);
    return buckets;
  };

  // Cluster the already-sorted/filtered list into groups per the category/tag
  // checkboxes, without disturbing the row order the user picked via column
  // sorting — only which bucket each row lands in, and the order of the
  // buckets themselves, are affected.
  const groupedExpenses = (() => {
    if (!groupByCategory && !groupByTag) return null;

    if (groupByCategory && groupByTag) {
      const catBuckets = byKey(mainExpenses, (e) => e.category || "Uncategorized");
      return Object.keys(catBuckets)
        .sort()
        .map((cat) => {
          const items = catBuckets[cat];
          const tagBuckets = byKey(items, (e) => e.tag || "No tag");
          const tagNames = sortTagNames(Object.keys(tagBuckets));
          const showTagHeaders = !(tagNames.length === 1 && tagNames[0] === "No tag");
          return {
            header: cat,
            subtotal: subtotalTextFor(items),
            subGroups: tagNames.map((tag) => ({
              label: showTagHeaders ? tag : null,
              items: tagBuckets[tag],
            })),
          };
        });
    }

    if (groupByCategory) {
      const catBuckets = byKey(mainExpenses, (e) => e.category || "Uncategorized");
      return Object.keys(catBuckets)
        .sort()
        .map((cat) => ({
          header: cat,
          subtotal: subtotalTextFor(catBuckets[cat]),
          subGroups: [{ label: null, items: catBuckets[cat] }],
        }));
    }

    // groupByTag only
    const tagBuckets = byKey(mainExpenses, (e) => e.tag || "No tag");
    return sortTagNames(Object.keys(tagBuckets)).map((tag) => ({
      header: tag,
      subtotal: subtotalTextFor(tagBuckets[tag]),
      subGroups: [{ label: null, items: tagBuckets[tag] }],
    }));
  })();

  // Shared row renderer — used by the main list (flat or grouped) and by
  // the collapsed archived-expenses section below it.
  const renderRow = (e) => (
    <li key={e.id} className="expense" style={{ gridTemplateColumns: gridCols }}>
      {e.image_path ? (
        <img
          className="thumb"
          src={`/uploads/${e.image_path}`}
          alt="receipt"
          title="View receipt"
          onClick={() => setViewing(e.image_path)}
        />
      ) : (
        <span className="thumb thumb-empty" title="No receipt">
          —
        </span>
      )}
      <div className="exp-fields" onClick={() => !readOnly && setEditing(e)}>
        {columns.date && (
          <span className="exp-col exp-col-date">{e.expense_date}</span>
        )}
        {columns.report && (
          <span
            className={`exp-col exp-col-tag badge ${e.report_title ? "assigned" : "unassigned"}`}
          >
            {e.report_title || "Unassigned"}
          </span>
        )}
        {columns.reportStatus && (
          <span
            className={`exp-col exp-col-status status ${e.report_status || ""}`}
          >
            {e.report_status || "—"}
          </span>
        )}
        {columns.merchant && (
          <strong className="exp-col exp-col-merchant">
            <span className="exp-col-merchant-name">
              {e.vendor || "Unknown"}
            </span>
            {duplicateIds.has(e.id) && (
              <span className="dup-badge" title="Same vendor, amount, and a nearby date as another expense">
                Duplicate
              </span>
            )}
          </strong>
        )}
        {columns.description && (
          <span className="exp-col exp-col-desc">{e.description}</span>
        )}
        {columns.category && (
          <span className="exp-col exp-col-category">{e.category}</span>
        )}
        {columns.tag && (
          <span className="exp-col exp-col-tag">{e.tag || ""}</span>
        )}
        <span className="exp-col exp-col-amount amount">
          {e.currency} {(e.amount || 0).toFixed(2)}
          {e._conv != null && (
            <span
              className="exp-conv"
              title={`${e._rate.toFixed(4)} ${e.currency}/${e._target_currency}`}
            >
              → {e._target_currency} {e._conv.toFixed(2)}
            </span>
          )}
        </span>
      </div>
      <div className="exp-actions">
        {!readOnly && !e.image_path && /→|->/.test(e.vendor || "") && (
          <button
            className="icon-btn"
            onClick={() => regen(e.id)}
            disabled={regenId === e.id}
            title="Generate mileage receipt"
          >
            {regenId === e.id ? "…" : <MapIcon className="icon" />}
          </button>
        )}
        {!readOnly && (
          <button
            className="icon-btn"
            onClick={() => remove(e.id)}
            title="Delete"
          >
            <XIcon className="icon" />
          </button>
        )}
      </div>
    </li>
  );

  const sortArrow = (key) =>
    sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "";
  const sortableHeader = (key, label) => (
    <span className="sortable-col" onClick={() => toggleSort(key)}>
      {label}
      {sortArrow(key)}
    </span>
  );

  // Same grid-template-columns on the header and every row keeps everything
  // lined up like a real table — columns come and go with the column
  // toggles above, so this list (and its header) has to be rebuilt from it
  // rather than hardcoded.
  // minmax(min, fr) instead of fixed px: columns grow to fill a wide screen
  // and shrink together (down to their floor) on a narrow one, rather than
  // staying a fixed width and forcing the row wider than the viewport.
  const gridCols = [
    "52px", // thumbnail
    columns.date && "minmax(88px,0.7fr)",
    columns.report && "minmax(100px,1.1fr)",
    columns.reportStatus && "minmax(90px,0.9fr)",
    columns.merchant && "minmax(120px,1.3fr)",
    columns.description && "minmax(120px,1.6fr)",
    columns.category && "minmax(100px,0.9fr)",
    columns.tag && "minmax(90px,0.9fr)",
    "minmax(90px,0.8fr)", // amount
    "72px", // actions
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section>
      <div className="filterbar">
        <select
          className="report-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="all">All reports</option>
          {reports
            .filter((r) => !r.archived_at || r.id === filter)
            .map((r) => (
              <option key={r.id} value={r.id}>
                {r.title}
                {r.archived_at ? " (archived)" : ""}
              </option>
            ))}
        </select>
        <span className="total">
          {filterActive
            ? `${filteredExpenses.length} of ${expenses.length} items · subtotal ${subtotalText || "0.00"}`
            : `${expenses.length} items · ${totalInfo.currency} ${totalInfo.total.toFixed(2)}`}
        </span>
        <div className="group-by-options" title="Group by">
          <span className="group-by-label">Group by</span>
          <label className="group-by-check">
            <input
              type="checkbox"
              checked={groupByCategory}
              onChange={(e) => setGroupByCategory(e.target.checked)}
            />
            Category
          </label>
          <label className="group-by-check">
            <input
              type="checkbox"
              checked={groupByTag}
              onChange={(e) => setGroupByTag(e.target.checked)}
            />
            Tag
          </label>
        </div>
        <button
          className="icon-btn"
          title="Columns"
          onClick={() => setShowColumns((v) => !v)}
        >
          <GearIcon className="icon" />
        </button>
      </div>

      <div className="filterbar">
        <input
          type="search"
          className="expense-search"
          placeholder="Search expenses…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          className={`icon-btn${showFilters ? " active" : ""}`}
          title="More filters"
          onClick={() => setShowFilters((v) => !v)}
        >
          <FilterIcon className="icon" />
        </button>
        {filterActive && (
          <button className="link-btn" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      {showFilters && (
        <div className="column-settings filter-panel">
          <Field label="Category">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Date from">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Field>
          <Field label="Date to">
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Field>
          <Field label="Amount from">
            <input
              type="number"
              step="0.01"
              placeholder="Any"
              value={amountMin}
              onChange={(e) => setAmountMin(e.target.value)}
            />
          </Field>
          <Field label="Amount to">
            <input
              type="number"
              step="0.01"
              placeholder="Any"
              value={amountMax}
              onChange={(e) => setAmountMax(e.target.value)}
            />
          </Field>
          <Field label="Status">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="unassigned">Unassigned</option>
              <option value="live">Live</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
              <option value="archived">Archived</option>
            </select>
          </Field>
          <Field label="Tag">
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            >
              <option value="all">All tags</option>
              {uniqueTags.map((tg) => (
                <option key={tg} value={tg}>
                  {tg}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Currency">
            <select
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
            >
              <option value="all">All currencies</option>
              {uniqueCurrencies.map((cur) => (
                <option key={cur} value={cur}>
                  {cur}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Receipt">
            <select
              value={receiptFilter}
              onChange={(e) => setReceiptFilter(e.target.value)}
            >
              <option value="all">Any</option>
              <option value="has">Has receipt</option>
              <option value="missing">Missing receipt</option>
            </select>
          </Field>
        </div>
      )}

      {showColumns && (
        <div className="column-settings">
          {OPTIONAL_COLUMNS.map((c) => (
            <label key={c.key} className="column-row">
              <input
                type="checkbox"
                checked={columns[c.key]}
                onChange={() => toggleColumn(c.key)}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
      )}

      {!readOnly && (
        <button
          className="add-manual"
          onClick={() => setEditing(blankExpense())}
        >
          ＋ Add expense manually
        </button>
      )}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : expenses.length === 0 ? (
        <p className="muted">No expenses yet. Scan a receipt to get started.</p>
      ) : filteredExpenses.length === 0 ? (
        <p className="muted">No expenses match your search.</p>
      ) : (
        <div className="exp-table">
          <div className="exp-header" style={{ gridTemplateColumns: gridCols }}>
            <span />
            {columns.date && sortableHeader("date", "Date")}
            {columns.report && sortableHeader("report", "Report")}
            {columns.reportStatus && sortableHeader("reportStatus", "Status")}
            {columns.merchant && sortableHeader("merchant", "Merchant")}
            {columns.description &&
              sortableHeader("description", "Description")}
            {columns.category && sortableHeader("category", "Category")}
            {columns.tag && sortableHeader("tag", "Tag")}
            <span className="exp-col-amount sortable-col" onClick={() => toggleSort("amount")}>
              Amount{sortArrow("amount")}
            </span>
            <span />
          </div>
          <ul className="list">
            {(() => {
              if (!groupedExpenses) return mainExpenses.map(renderRow);

              return groupedExpenses.flatMap((g) => [
                <li
                  key={`grp-${g.header}`}
                  className="group-header group-header-category"
                >
                  <span>{g.header}</span>
                  <span className="group-subtotal">{g.subtotal}</span>
                </li>,
                ...g.subGroups.flatMap((sg) => [
                  ...(sg.label
                    ? [
                        <li
                          key={`grp-${g.header}-${sg.label}`}
                          className="group-header group-header-tag"
                        >
                          {sg.label}
                        </li>,
                      ]
                    : []),
                  ...sg.items.map(renderRow),
                ]),
              ]);
            })()}
          </ul>
        </div>
      )}

      {archivedExpenses.length > 0 && (
        <>
          <button
            className="link-btn"
            style={{ margin: "12px 0 8px" }}
            onClick={() => setShowArchivedExpenses((v) => !v)}
          >
            {showArchivedExpenses ? "▾" : "▸"} Archived expenses (
            {archivedExpenses.length})
          </button>
          {showArchivedExpenses && (
            <div className="exp-table">
              <div className="exp-header" style={{ gridTemplateColumns: gridCols }}>
                <span />
                {columns.date && sortableHeader("date", "Date")}
                {columns.report && sortableHeader("report", "Report")}
                {columns.reportStatus && sortableHeader("reportStatus", "Status")}
                {columns.merchant && sortableHeader("merchant", "Merchant")}
                {columns.description &&
                  sortableHeader("description", "Description")}
                {columns.category && sortableHeader("category", "Category")}
                {columns.tag && sortableHeader("tag", "Tag")}
                <span className="exp-col-amount sortable-col" onClick={() => toggleSort("amount")}>
                  Amount{sortArrow("amount")}
                </span>
                <span />
              </div>
              <ul className="list">{archivedExpenses.map(renderRow)}</ul>
            </div>
          )}
        </>
      )}

      {editing && (
        <EditModal
          key={editing.id || "new"}
          expense={editing}
          categories={categories}
          reports={reports}
          goToReports={goToReports}
          onNav={
            editing.id
              ? (dir) => {
                  const idx = filteredExpenses.findIndex(
                    (x) => x.id === editing.id,
                  );
                  const next = filteredExpenses[idx + dir];
                  if (next) setEditing(next);
                }
              : null
          }
          hasPrev={
            editing.id &&
            filteredExpenses.findIndex((x) => x.id === editing.id) > 0
          }
          hasNext={
            editing.id &&
            filteredExpenses.findIndex((x) => x.id === editing.id) <
              filteredExpenses.length - 1
          }
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {viewing && (
        <div className="modal-backdrop" onClick={() => setViewing(null)}>
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <img src={`/uploads/${viewing}`} alt="receipt" />
            <a
              className="secondary"
              href={`/uploads/${viewing}`}
              target="_blank"
              rel="noreferrer"
            >
              Open full size
            </a>
          </div>
        </div>
      )}
    </section>
  );
}

function EditModal({
  expense,
  categories,
  reports,
  goToReports,
  onNav,
  hasPrev,
  hasNext,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({ ...expense });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const isNew = !expense.id;

  useEffect(() => {
    if (!onNav) return;
    function onKeyDown(e) {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (e.key === "ArrowLeft" && hasPrev) onNav(-1);
      else if (e.key === "ArrowRight" && hasNext) onNav(1);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onNav, hasPrev, hasNext]);

  async function save() {
    setSaving(true);
    try {
      if (isNew) await api.createExpense(form);
      else await api.updateExpense(expense.id, form);
      onSaved();
    } catch (err) {
      alert(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isNew ? "Add expense" : "Edit expense"}</h3>
          {onNav && (
            <div className="modal-nav">
              <button
                type="button"
                className="icon-btn"
                onClick={() => onNav(-1)}
                disabled={!hasPrev}
                title="Previous expense (←)"
              >
                ‹
              </button>
              <button
                type="button"
                className="icon-btn"
                onClick={() => onNav(1)}
                disabled={!hasNext}
                title="Next expense (→)"
              >
                ›
              </button>
            </div>
          )}
        </div>
        <ReportPicker
          value={form.report_id}
          onChange={(v) => setForm((f) => ({ ...f, report_id: v }))}
          reports={reports}
          onCreateNew={goToReports}
        />
        <Field label="Merchant">
          <input value={form.vendor || ""} onChange={set("vendor")} />
        </Field>
        <div className="row">
          <Field label="Date">
            <input
              type="date"
              value={form.expense_date || ""}
              onChange={set("expense_date")}
            />
          </Field>
          <Field label="Category">
            <select value={form.category || "Other"} onChange={set("category")}>
              {categories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="row">
          <Field label="Total">
            <input
              type="number"
              step="0.01"
              value={form.amount ?? ""}
              onChange={set("amount")}
            />
          </Field>
          <Field label="Currency">
            <input value={form.currency || "EUR"} onChange={set("currency")} />
          </Field>
        </div>
        <Field label="VAT amount">
          <input
            type="number"
            step="0.01"
            value={form.vat_amount ?? ""}
            onChange={set("vat_amount")}
          />
        </Field>
        <Field label="Description">
          <input value={form.description || ""} onChange={set("description")} />
        </Field>
        <Field label="Tag">
          <input
            value={form.tag || ""}
            onChange={set("tag")}
            placeholder="Optional label"
          />
        </Field>
        <Field label="Notes">
          <input value={form.notes || ""} onChange={set("notes")} />
        </Field>
        <div className="actions">
          <button className="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Reports ---------------- */

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
function ReportsView({
  reports,
  reloadReports,
  onOpenReport,
  readOnly,
  defaultSubmissionEmail,
}) {
  const years = [];
  for (let y = CURRENT_YEAR + 1; y >= CURRENT_YEAR - 5; y--) years.push(y);

  const [editingReport, setEditingReport] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const activeReports = reports.filter((r) => !r.archived_at);
  const archivedReports = reports.filter((r) => r.archived_at);

  function downloadReport(r) {
    window.open(api.reportPdfUrl(r.id), "_blank", "noopener");
  }

  function downloadReportCsv(r) {
    window.open(api.reportCsvUrl(r.id), "_blank", "noopener");
  }

  async function submitReport(r) {
    await api.updateReport(r.id, { status: "submitted" });
    reloadReports();
  }

  async function archiveReport(r) {
    await api.archiveReport(r.id);
    reloadReports();
  }

  async function unarchiveReport(r) {
    await api.unarchiveReport(r.id);
    reloadReports();
  }

  // Email-a-report modal state.
  const [emailingReport, setEmailingReport] = useState(null);
  const [submitEmailAddr, setSubmitEmailAddr] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [attachCsv, setAttachCsv] = useState(false);
  const [submitNote, setSubmitNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  function openEmailSubmit(r) {
    setEmailingReport(r);
    setSubmitEmailAddr(r.submitted_to || defaultSubmissionEmail || "");
    setAttachPdf(true);
    setAttachCsv(false);
    setSubmitNote("");
    setSubmitError("");
  }

  async function confirmEmailSubmit() {
    if (!submitEmailAddr.trim()) {
      setSubmitError("Enter a recipient email.");
      return;
    }
    const invalid = submitEmailAddr
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean)
      .find((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (invalid) {
      setSubmitError(`"${invalid}" doesn't look like a valid email.`);
      return;
    }
    if (!attachPdf && !attachCsv) {
      setSubmitError("Pick at least one attachment: PDF, CSV, or both.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      await api.submitReport(emailingReport.id, {
        email: submitEmailAddr.trim(),
        attachPdf,
        attachCsv,
        note: submitNote.trim(),
      });
      await reloadReports();
      setEmailingReport(null);
    } catch (err) {
      setSubmitError(err.message || "Could not send report");
    } finally {
      setSubmitting(false);
    }
  }

  async function removeReport(r) {
    if (!confirm(`Delete report “${r.title}”? (expenses are kept)`)) return;
    try {
      await api.deleteReport(r.id);
      await reloadReports();
    } catch (err) {
      alert(err.message || "Could not delete report");
    }
  }

  // Create form
  const [period, setPeriod] = useState("quarter"); // quarter | month
  const [year, setYear] = useState(CURRENT_YEAR);
  const [quarter, setQuarter] = useState(currentQuarter());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [title, setTitle] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [creating, setCreating] = useState(false);

  const value = period === "quarter" ? quarter : period === "year" ? 1 : month;

  const [createError, setCreateError] = useState("");

  // Same grid-template-columns on the header and every row, mirroring the
  // Expenses table's layout (date / status / title / total / actions).
  const gridCols =
    "minmax(88px,0.8fr) minmax(90px,0.9fr) 2fr minmax(100px,0.9fr) 252px";

  async function createReport() {
    setCreateError("");
    setCreating(true);
    try {
      const def = await api.createReport({
        period,
        year,
        value,
        title,
        currency,
      });
      await reloadReports();
      setShowCreate(false);
      setTitle("");
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <section className="card">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h3 style={{ margin: 0 }}>Reports</h3>
        {!readOnly && (
          <button
            className="primary btn-compact"
            onClick={() => setShowCreate(true)}
          >
            ＋ New report
          </button>
        )}
      </div>

      {archivedReports.length > 0 && (
        <button
          className="link-btn"
          style={{ margin: "4px 0 8px" }}
          onClick={() => setShowArchived((v) => !v)}
        >
          {showArchived ? "▾" : "▸"} Archived reports ({archivedReports.length})
        </button>
      )}

      {showCreate && (
        <div
          className="modal-backdrop"
          onClick={() => !creating && setShowCreate(false)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create new report</h3>
            <Field label="Period">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              >
                <option value="quarter">Quarterly</option>
                <option value="month">Monthly</option>
                <option value="year">Annual</option>
              </select>
            </Field>

            <div className="row">
              <Field label="Year">
                <select
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                >
                  {years.map((y) => (
                    <option key={y}>{y}</option>
                  ))}
                </select>
              </Field>
              {period === "quarter" && (
                <Field label="Quarter">
                  <select
                    value={quarter}
                    onChange={(e) => setQuarter(Number(e.target.value))}
                  >
                    {[1, 2, 3, 4].map((q) => (
                      <option key={q} value={q}>
                        Q{q}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {period === "month" && (
                <Field label="Month">
                  <select
                    value={month}
                    onChange={(e) => setMonth(Number(e.target.value))}
                  >
                    {MONTH_NAMES.map((m, i) => (
                      <option key={m} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            </div>

            <div className="row">
              <Field label="Report name (title)">
                <input
                  value={title}
                  placeholder={
                    period === "quarter"
                      ? `${year} Q${quarter}`
                      : period === "year"
                        ? `${year}`
                        : `${MONTH_NAMES[month - 1]} ${year}`
                  }
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>
              <Field label="Currency">
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <option value="GBP">GBP £</option>
                  <option value="EUR">EUR €</option>
                  <option value="USD">USD $</option>
                </select>
              </Field>
            </div>
            <p className="muted" style={{ fontSize: 12, margin: "-4px 0 8px" }}>
              Expenses in other currencies convert to {currency} at the
              current rate.
            </p>

            {createError && <div className="error">{createError}</div>}

            <div className="actions">
              <button
                className="secondary"
                onClick={() => setShowCreate(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                className="primary"
                onClick={createReport}
                disabled={creating}
              >
                {creating ? "Creating…" : "＋ Create report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active reports */}
      {activeReports.length === 0 ? (
        <p className="muted">
          {reports.length === 0
            ? "No reports yet — click “＋ New report” to create one."
            : "No active reports — see Archived reports above."}
        </p>
      ) : (
        <div className="exp-table">
          <div className="exp-header" style={{ gridTemplateColumns: gridCols }}>
            <span>Date</span>
            <span>Status</span>
            <span>Title</span>
            <span className="exp-col-amount">Total</span>
            <span />
          </div>
          <ul className="list">
            {activeReports.map((r) => (
              <ReportRow
                key={r.id}
                r={r}
                gridCols={gridCols}
                readOnly={readOnly}
                onOpenReport={onOpenReport}
                downloadReport={downloadReport}
                downloadReportCsv={downloadReportCsv}
                openEmailSubmit={openEmailSubmit}
                submitReport={submitReport}
                archiveReport={archiveReport}
                unarchiveReport={unarchiveReport}
                setEditingReport={setEditingReport}
                removeReport={removeReport}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Archived reports — same table, tucked away behind the toggle above
          so the active list above stays uncluttered once reports pile up. */}
      {showArchived && archivedReports.length > 0 && (
        <div className="exp-table" style={{ marginTop: 16 }}>
          <div className="exp-header" style={{ gridTemplateColumns: gridCols }}>
            <span>Date</span>
            <span>Status</span>
            <span>Title</span>
            <span className="exp-col-amount">Total</span>
            <span />
          </div>
          <ul className="list">
            {archivedReports.map((r) => (
              <ReportRow
                key={r.id}
                r={r}
                gridCols={gridCols}
                readOnly={readOnly}
                onOpenReport={onOpenReport}
                downloadReport={downloadReport}
                downloadReportCsv={downloadReportCsv}
                openEmailSubmit={openEmailSubmit}
                submitReport={submitReport}
                archiveReport={archiveReport}
                unarchiveReport={unarchiveReport}
                setEditingReport={setEditingReport}
                removeReport={removeReport}
              />
            ))}
          </ul>
        </div>
      )}

      {editingReport && (
        <EditReportModal
          report={editingReport}
          onClose={() => setEditingReport(null)}
          onSaved={() => {
            setEditingReport(null);
            reloadReports();
          }}
        />
      )}

      {emailingReport && (
        <div
          className="modal-backdrop"
          onMouseDown={(e) =>
            e.target === e.currentTarget && !submitting && setEmailingReport(null)
          }
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Email “{emailingReport.title}”</h3>
            <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
              Sends the picked attachment(s), and marks this report
              "submitted" once it's actually sent.
            </p>
            <Field label="Recipient email(s)">
              <input
                type="email"
                multiple
                value={submitEmailAddr}
                onChange={(e) => setSubmitEmailAddr(e.target.value)}
                placeholder="bookkeeper@example.com, cfo@example.com"
              />
            </Field>
            <Field label="Message (optional)">
              <textarea
                rows={4}
                maxLength={2000}
                value={submitNote}
                onChange={(e) => setSubmitNote(e.target.value)}
                placeholder="Add a note explaining this report — it appears at the top of the email."
              />
            </Field>
            <div className="group-by-options" title="Attach">
              <span className="group-by-label">Attach</span>
              <label className="group-by-check">
                <input
                  type="checkbox"
                  checked={attachPdf}
                  onChange={(e) => setAttachPdf(e.target.checked)}
                />
                PDF
              </label>
              <label className="group-by-check">
                <input
                  type="checkbox"
                  checked={attachCsv}
                  onChange={(e) => setAttachCsv(e.target.checked)}
                />
                CSV
              </label>
            </div>
            {submitError && <div className="error">{submitError}</div>}
            <div className="actions">
              <button
                className="secondary"
                onClick={() => setEmailingReport(null)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="primary"
                onClick={confirmEmailSubmit}
                disabled={submitting || (!attachPdf && !attachCsv)}
              >
                {submitting ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// One row shared by the active and archived reports tables.
function ReportRow({
  r,
  gridCols,
  readOnly,
  onOpenReport,
  downloadReport,
  downloadReportCsv,
  openEmailSubmit,
  submitReport,
  archiveReport,
  unarchiveReport,
  setEditingReport,
  removeReport,
}) {
  const archived = !!r.archived_at;
  const canArchive = !archived;
  return (
    <li className="expense" style={{ gridTemplateColumns: gridCols }}>
      <div
        className="exp-fields"
        onClick={() => onOpenReport(r.id)}
        title="View this report's expenses"
      >
        <span className="exp-col exp-col-date">
          {(r.created_at || "").slice(0, 10)}
        </span>
        <span className={`exp-col exp-col-status status ${r.status}`}>
          {r.status}
        </span>
        <strong
          className="exp-col exp-col-merchant"
          title={
            r.submitted_to
              ? `Emailed to ${r.submitted_to} on ${(r.submitted_at || "").slice(0, 10)}`
              : undefined
          }
        >
          {r.title}
        </strong>
        <span className="exp-col exp-col-amount amount">
          {r.currency} {(r.total || 0).toFixed(2)}
        </span>
      </div>
      <div className="exp-actions">
        <button
          className="icon-btn"
          onClick={() => downloadReport(r)}
          title="Download PDF"
        >
          <DownloadIcon className="icon" />
        </button>
        <button
          className="icon-btn"
          onClick={() => downloadReportCsv(r)}
          title="Download CSV"
        >
          <TableIcon className="icon" />
        </button>
        {!readOnly && !archived && (
          <button
            className="icon-btn"
            onClick={() => openEmailSubmit(r)}
            title="Email report to submit it"
          >
            <MailIcon className="icon" />
          </button>
        )}
        {!readOnly && !archived && r.status === "live" && (
          <button
            className="icon-btn"
            onClick={() => submitReport(r)}
            title="Mark as submitted without emailing"
          >
            <CheckCircleIcon className="icon" />
          </button>
        )}
        {!readOnly && canArchive && (
          <button
            className="icon-btn"
            onClick={() => archiveReport(r)}
            title="Archive this report"
          >
            <ArchiveIcon className="icon" />
          </button>
        )}
        {!readOnly && archived && (
          <button
            className="icon-btn"
            onClick={() => unarchiveReport(r)}
            title="Move back to Reports"
          >
            <UnarchiveIcon className="icon" />
          </button>
        )}
        {!readOnly && (
          <button
            className="icon-btn"
            onClick={() => setEditingReport(r)}
            title="Edit report"
          >
            <PencilIcon className="icon" />
          </button>
        )}
        {!readOnly && (
          <button
            className="icon-btn"
            onClick={() => removeReport(r)}
            title="Delete report"
          >
            <XIcon className="icon" />
          </button>
        )}
      </div>
    </li>
  );
}

function EditReportModal({ report, onClose, onSaved }) {
  const [title, setTitle] = useState(report.title);
  const [currency, setCurrency] = useState(report.currency);
  const [status, setStatus] = useState(report.status);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    setSaving(true);
    setError("");
    try {
      await api.updateReport(report.id, { title, currency, status });
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Edit report</h3>
        {error && <div className="error">{error}</div>}
        <Field label="Report name">
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <div className="row">
          <Field label="Currency">
            <select
              value={currency}
              disabled={!!report.submitted_at}
              onChange={(e) => setCurrency(e.target.value)}
            >
              <option value="GBP">GBP £</option>
              <option value="EUR">EUR €</option>
              <option value="USD">USD $</option>
            </select>
          </Field>
          <Field label="Status">
            <select
              value={status}
              disabled={report.status === "archived"}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="live">live</option>
              <option value="submitted">submitted</option>
              <option value="approved">approved</option>
              <option value="paid">paid</option>
            </select>
          </Field>
        </div>
        {report.status === "archived" && (
          <p className="muted" style={{ fontSize: 12, margin: "-4px 0 8px" }}>
            This report is archived — unarchive it to change its status.
          </p>
        )}
        {report.submitted_at && (
          <p className="muted" style={{ fontSize: 12, margin: "-4px 0 8px" }}>
            Currency is locked — this report has already been submitted.
          </p>
        )}
        <div className="actions">
          <button className="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- helpers ---------------- */

function Field({ label, children }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

// Dropdown to assign an expense to one of the saved reports.
// `value` is a report id (or "" for unassigned).
// A report an expense can still usefully be assigned to — archived and
// submitted reports are no longer live.
function isAssignableReport(r) {
  return r.status !== "archived" && r.status !== "submitted";
}

// Default report for a brand-new expense: the most recent live one, never
// a submitted/archived report picked up by taking reports[0] (listReportDefs
// sorts by year/value/created_at, not status).
function defaultReportId(reports) {
  return reports.find(isAssignableReport)?.id || "";
}

function ReportPicker({ value, onChange, reports, onCreateNew }) {
  const t = useT();
  // Hide archived/submitted reports, unless one is the expense's *existing*
  // assignment (editing an expense already on a now-closed report) — then
  // keep it visible so the picker doesn't silently drop that selection.
  const assignable = reports.filter((r) => isAssignableReport(r) || r.id === value);
  return (
    <Field label={t("app.fieldReport")}>
      <select
        value={value || ""}
        onChange={(e) => {
          if (e.target.value === "__new__") onCreateNew?.();
          else onChange(e.target.value);
        }}
      >
        <option value="">{t("app.reportNone")}</option>
        {assignable.map((r) => (
          <option key={r.id} value={r.id}>
            {r.title}
          </option>
        ))}
        {onCreateNew && <option value="__new__">{t("app.reportCreateNew")}</option>}
      </select>
    </Field>
  );
}

function currentQuarter() {
  return Math.floor(new Date().getMonth() / 3) + 1;
}

function blankExpense() {
  return {
    report_id: "",
    vendor: "",
    expense_date: new Date().toISOString().slice(0, 10),
    amount: "",
    currency: "EUR",
    vat_amount: "",
    category: "Other",
    description: "",
    tag: "",
    notes: "",
  };
}
